import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, FileText, EnvelopeSimple, CaretDown, CaretUp, X } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * "Matériel de chantier" section displayed inside the project Gestion tab.
 *
 * Lists every quote recap (one per accepted quote) for the project, and
 * lets admins:
 *   - Open a recap to edit per-line received quantity, delivery date, supplier
 *   - Send a price-quote email to a supplier with selected materials
 */
export const ProjectRecapsSection = ({ project }) => {
  const [recaps, setRecaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openRecap, setOpenRecap] = useState(null); // full recap object
  const [emailModal, setEmailModal] = useState(null); // { recap, supplier_id, lines:Set, message }
  const [suppliers, setSuppliers] = useState([]);

  const fetchRecaps = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/recaps/by-project/${project.id}`, { withCredentials: true });
      setRecaps(r.data || []);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  const fetchSuppliers = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/suppliers`, { withCredentials: true });
      setSuppliers(r.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { if (project?.id) { fetchRecaps(); fetchSuppliers(); } }, [project?.id]);

  if (loading) return <div className="card" style={{ padding: 14 }}>Chargement...</div>;

  return (
    <div className="card" style={{ padding: 14, marginTop: 16 }} data-testid="recaps-section">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <Package size={20} color="#002FA7" weight="bold" />
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Matériel de chantier</h3>
        <span style={{ fontSize: '0.78rem', color: '#5E5F66' }}>
          Récap automatique des devis validés ({recaps.length})
        </span>
        {recaps.length > 0 && (() => {
          const totalEUR = recaps.reduce((s, r) => s + (r.materials || []).reduce((s2, m) => s2 + (Number(m.quantity_required) || 0) * (Number(m.unit_price) || 0), 0), 0);
          const totalRcvEUR = recaps.reduce((s, r) => s + (r.materials || []).reduce((s2, m) => s2 + (Number(m.quantity_received) || 0) * (Number(m.unit_price) || 0), 0), 0);
          const totalHrs = recaps.reduce((s, r) => s + (Number(r.total_hours) || 0), 0);
          return (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }} data-testid="recaps-kpi">
              <div style={{ fontSize: '0.78rem', color: '#5E5F66' }}>
                Total matériaux requis : <strong style={{ color: '#002FA7', fontSize: '0.95rem' }}>{totalEUR.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €</strong>
              </div>
              <div style={{ fontSize: '0.78rem', color: '#5E5F66' }}>
                Livré : <strong style={{ color: '#16A34A', fontSize: '0.95rem' }}>{totalRcvEUR.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €</strong>
              </div>
              <div style={{ fontSize: '0.78rem', color: '#5E5F66' }}>
                MO : <strong style={{ color: '#92400E', fontSize: '0.95rem' }}>{totalHrs.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} h</strong>
              </div>
            </div>
          );
        })()}
      </div>
      {recaps.length === 0 && (
        <div style={{ padding: 14, color: '#5E5F66', fontSize: '0.85rem', background: '#F4F5F7', borderRadius: 8 }}>
          Aucun devis validé pour ce chantier. Validez un devis pour générer automatiquement la fiche récap des matériaux.
        </div>
      )}
      {recaps.map(rec => (
        <RecapRow key={rec.id} recap={rec} suppliers={suppliers}
          onOpen={async () => {
            const r = await axios.get(`${API_URL}/api/recaps/${rec.id}`, { withCredentials: true });
            setOpenRecap(r.data);
          }}
          onAskPrice={async () => {
            const r = await axios.get(`${API_URL}/api/recaps/${rec.id}`, { withCredentials: true });
            setEmailModal({ recap: r.data, supplier_id: '', lines: new Set((r.data.materials || []).map(m => m.id)), message: '' });
          }}
        />
      ))}

      {openRecap && (
        <RecapEditModal
          recap={openRecap}
          suppliers={suppliers}
          onClose={() => setOpenRecap(null)}
          onSaved={() => { fetchRecaps(); }}
        />
      )}
      {emailModal && (
        <PriceQuoteEmailModal
          recap={emailModal.recap}
          suppliers={suppliers}
          onClose={() => setEmailModal(null)}
        />
      )}
    </div>
  );
};

const RecapRow = ({ recap, onOpen, onAskPrice }) => {
  const totalLines = (recap.materials || []).length;
  const totalReq = (recap.materials || []).reduce((s, m) => s + (Number(m.quantity_required) || 0), 0);
  const totalRcv = (recap.materials || []).reduce((s, m) => s + (Number(m.quantity_received) || 0), 0);
  return (
    <div data-testid={`recap-row-${recap.id}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', border: '1px solid #E2E2E6', borderRadius: 8, marginBottom: 8, background: '#FAFBFD' }}>
      <FileText size={18} color="#002FA7" />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>{recap.quote_number}</div>
        <div style={{ fontSize: '0.78rem', color: '#5E5F66' }}>
          {totalLines} matériaux · MO {recap.total_hours} h · livré {totalRcv.toLocaleString('fr-FR')}/{totalReq.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}
        </div>
      </div>
      <button data-testid={`recap-ask-price-${recap.id}`} className="btn btn-secondary btn-sm" onClick={onAskPrice} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <EnvelopeSimple size={14} /> Demande de prix
      </button>
      <button data-testid={`recap-open-${recap.id}`} className="btn btn-primary btn-sm" onClick={onOpen}>
        Voir / Saisir
      </button>
    </div>
  );
};

const RecapEditModal = ({ recap, suppliers, onClose, onSaved }) => {
  const [materials, setMaterials] = useState(recap.materials || []);
  const [savingId, setSavingId] = useState(null);

  const updateLine = (id, patch) => {
    setMaterials(prev => prev.map(m => m.id === id ? { ...m, ...patch } : m));
  };

  const persistLine = async (line) => {
    setSavingId(line.id);
    try {
      const sup = suppliers.find(s => s.id === line.supplier_id);
      await axios.put(
        `${API_URL}/api/recaps/${recap.id}/material/${line.id}`,
        {
          quantity_received: Number(line.quantity_received) || 0,
          delivery_date: line.delivery_date || '',
          supplier_id: line.supplier_id || '',
          supplier_name: sup ? sup.name : (line.supplier_name || ''),
          notes: line.notes || '',
          unit_price: Number(line.unit_price) || 0,
        },
        { withCredentials: true },
      );
      toast.success('Ligne enregistrée', { duration: 1200 });
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur sauvegarde');
    } finally { setSavingId(null); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 1100, maxHeight: '90vh', overflow: 'auto', padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <FileText size={20} color="#002FA7" />
          <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Récap matériel — {recap.quote_number}</h2>
          <span style={{ fontSize: '0.78rem', color: '#5E5F66' }}>Chantier : {recap.project_name}</span>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }} data-testid="recap-edit-close"><X size={22} /></button>
        </div>
        <table className="data-table" style={{ width: '100%', fontSize: '0.85rem' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Matériau</th>
              <th>Unité</th>
              <th style={{ textAlign: 'right' }}>Qté requise</th>
              <th style={{ textAlign: 'right' }}>Qté livrée</th>
              <th>Date livraison</th>
              <th>Fournisseur</th>
              <th style={{ textAlign: 'right' }}>PU HT</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {materials.map(m => {
              const reqv = Number(m.quantity_required) || 0;
              const rcvv = Number(m.quantity_received) || 0;
              const pct = reqv > 0 ? Math.min(100, (rcvv / reqv) * 100) : 0;
              return (
                <tr key={m.id}>
                  <td style={{ fontWeight: 600 }}>
                    {m.name}
                    <div style={{ height: 4, background: '#E2E2E6', borderRadius: 2, marginTop: 4, maxWidth: 180 }}>
                      <div style={{ height: '100%', background: pct >= 100 ? '#16A34A' : '#F59E0B', width: `${pct}%`, borderRadius: 2 }} />
                    </div>
                  </td>
                  <td>{m.unit}</td>
                  <td style={{ textAlign: 'right' }}>{Number(m.quantity_required).toLocaleString('fr-FR', { maximumFractionDigits: 3 })}</td>
                  <td>
                    <input
                      data-testid={`mat-rcv-${m.id}`}
                      type="number" step="0.01" min="0" className="form-input"
                      value={m.quantity_received || ''}
                      onChange={(e) => updateLine(m.id, { quantity_received: parseFloat(e.target.value) || 0 })}
                      style={{ width: 90, textAlign: 'right' }}
                    />
                  </td>
                  <td>
                    <input
                      type="date" className="form-input"
                      value={m.delivery_date || ''}
                      onChange={(e) => updateLine(m.id, { delivery_date: e.target.value })}
                      style={{ width: 140 }}
                    />
                  </td>
                  <td>
                    <Select value={m.supplier_id || '__none__'} onValueChange={(v) => updateLine(m.id, { supplier_id: v === '__none__' ? '' : v })}>
                      <SelectTrigger style={{ width: 180, height: 34, fontSize: '0.82rem' }}><SelectValue placeholder="-" /></SelectTrigger>
                      <SelectContent className="z-[9999]">
                        <SelectItem value="__none__">—</SelectItem>
                        {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td>
                    <input
                      type="number" step="0.01" min="0" className="form-input"
                      value={m.unit_price || ''}
                      onChange={(e) => updateLine(m.id, { unit_price: parseFloat(e.target.value) || 0 })}
                      style={{ width: 80, textAlign: 'right' }}
                    />
                  </td>
                  <td>
                    <button
                      data-testid={`mat-save-${m.id}`}
                      className="btn btn-primary btn-sm"
                      onClick={() => persistLine(m)}
                      disabled={savingId === m.id}
                    >
                      {savingId === m.id ? '…' : 'OK'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn btn-secondary">Fermer</button>
        </div>
      </div>
    </div>
  );
};

const PriceQuoteEmailModal = ({ recap, suppliers, onClose }) => {
  const [supplierId, setSupplierId] = useState('');
  const [selected, setSelected] = useState(new Set((recap.materials || []).map(m => m.id)));
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const supplier = useMemo(() => suppliers.find(s => s.id === supplierId), [suppliers, supplierId]);
  const linesPreview = useMemo(() => (recap.materials || []).filter(m => selected.has(m.id)), [recap, selected]);

  const toggle = (id) => {
    setSelected(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };

  const send = async () => {
    if (!supplierId) { toast.error('Choisir un fournisseur'); return; }
    if (!supplier?.email) { toast.error("Le fournisseur n'a pas d'email enregistré"); return; }
    if (selected.size === 0) { toast.error('Sélectionnez au moins une ligne'); return; }
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/recaps/${recap.id}/send-price-quote-email`, {
        supplier_id: supplierId,
        material_line_ids: Array.from(selected),
        custom_message: message || '',
      }, { withCredentials: true });
      toast.success(`Email envoyé à ${supplier.email}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'envoi");
    } finally { setSending(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }}>
      <div style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 720, maxHeight: '90vh', overflow: 'auto', padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <EnvelopeSimple size={22} color="#002FA7" />
          <h2 style={{ margin: 0, fontSize: '1.05rem' }}>Demande de prix — {recap.quote_number}</h2>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }} data-testid="askprice-close"><X size={22} /></button>
        </div>

        <div className="form-group">
          <label className="form-label">Fournisseur</label>
          <Select value={supplierId || '__none__'} onValueChange={(v) => setSupplierId(v === '__none__' ? '' : v)}>
            <SelectTrigger data-testid="askprice-supplier-select"><SelectValue placeholder="Choisir un fournisseur" /></SelectTrigger>
            <SelectContent className="z-[9999]">
              <SelectItem value="__none__">—</SelectItem>
              {suppliers.map(s => <SelectItem key={s.id} value={s.id} disabled={!s.email}>{s.name}{s.email ? '' : ' (sans email)'}</SelectItem>)}
            </SelectContent>
          </Select>
          {supplier && !supplier.email && <small style={{ color: '#DC2626' }}>Ce fournisseur n'a pas d'email enregistré.</small>}
          {supplier && supplier.email && <small style={{ color: '#5E5F66' }}>Sera envoyé à : <strong>{supplier.email}</strong></small>}
        </div>

        <div className="form-group">
          <label className="form-label">Matériaux à inclure ({selected.size}/{recap.materials.length})</label>
          <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #E2E2E6', borderRadius: 8, padding: 8 }}>
            {(recap.materials || []).map(m => (
              <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px', cursor: 'pointer', fontSize: '0.85rem' }}>
                <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggle(m.id)} />
                <span style={{ flex: 1 }}>{m.name}</span>
                <span style={{ color: '#5E5F66' }}>{Number(m.quantity_required).toLocaleString('fr-FR', { maximumFractionDigits: 3 })} {m.unit}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Message complémentaire (facultatif)</label>
          <textarea className="form-input" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Ex : livraison souhaitée la semaine prochaine, conditions de paiement..." />
        </div>

        <details style={{ background: '#F4F5F7', padding: 10, borderRadius: 8, marginTop: 8 }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>Aperçu du message ({linesPreview.length} lignes)</summary>
          <div style={{ fontSize: '0.78rem', color: '#374151', marginTop: 8 }}>
            <p>Bonjour {supplier?.contact_name || supplier?.name || 'Fournisseur'},</p>
            <p>Dans le cadre de notre chantier <strong>{recap.project_name}</strong>, nous souhaitons obtenir
            un devis pour les matériaux suivants&nbsp;:</p>
            <ul>
              {linesPreview.slice(0, 8).map(m => (
                <li key={m.id}>{m.name} — {Number(m.quantity_required).toLocaleString('fr-FR', { maximumFractionDigits: 3 })} {m.unit}</li>
              ))}
              {linesPreview.length > 8 && <li>... +{linesPreview.length - 8} autres</li>}
            </ul>
            {message && <p>{message}</p>}
            <p>Merci de nous communiquer votre meilleure offre…<br/>Cordialement, L'équipe BATI-BOYS.</p>
          </div>
        </details>

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn btn-secondary">Annuler</button>
          <button data-testid="askprice-send-btn" onClick={send} disabled={sending || !supplierId || selected.size === 0 || !supplier?.email} className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <EnvelopeSimple size={16} weight="bold" />
            {sending ? 'Envoi...' : 'Envoyer la demande'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectRecapsSection;
